// AWS costs view model (SCR-012, FLOW-007, API-034/035/036). Pure: the cost-basis rows (fleet
// default + per-customer), CUR source and usage-type map rows and their new-record validation, the
// AWS accounts a CUR source can live in, and the customer-month page's states — loading, empty
// ("link an AWS account first"), pending finalization, unmapped rows, and finalized (read-only,
// digest shown) with its restatement.
import type { components } from "../../api/schema";
import type { ApiClient } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { forceReason, month, required, validate, type FieldErrors } from "@nexus/ui";
import { addMonths, currentMonth } from "@nexus/ui";

export type ExpenseBasisKind = components["schemas"]["ExpenseBasisKind"];
export type ExpenseBasis = components["schemas"]["ExpenseBasis"];
export type ExpenseBasisEntry = components["schemas"]["ExpenseBasisEntry"];
export type CurSource = components["schemas"]["CurSource"];
export type CurSourceStatus = components["schemas"]["CurSourceStatus"];
export type CurUsageType = components["schemas"]["CurUsageType"];
export type TokenClass = components["schemas"]["TokenClass"];
export type ExpenseReconciliation = components["schemas"]["ExpenseReconciliation"];
export type ExpenseReconciliationAccount = components["schemas"]["ExpenseReconciliationAccount"];
export type ExpenseUnmappedRow = components["schemas"]["ExpenseUnmappedRow"];
export type ExpenseSnapshot = components["schemas"]["ExpenseSnapshot"];
export type AwsAccountLink = components["schemas"]["AwsAccountLink"];

export const EXPENSE_PATH = "/aws-costs";
export const EXPENSE_PERIOD_PATH = "/aws-costs/:orgId/:period" as const;

export const BASIS_KIND_LABELS: Readonly<Record<ExpenseBasisKind, string>> = {
  list: "AWS list price",
  cur: "Actual AWS bill (CUR)",
};

export const CUR_SOURCE_STATUS_LABELS: Readonly<Record<CurSourceStatus, string>> = {
  pending_verification: "Pending verification",
  active: "Active",
  failing: "Failing",
  disabled: "Disabled",
};

export const LINK_ACCOUNT_FIRST_MESSAGE = "Link an AWS account first.";

// --- Expense basis (fleet default + per-org overrides) ------------------------------------------

export interface BasisRowView {
  effectiveMonth: string;
  label: string;
  isFleetDefault: boolean;
}

/** One row per effective-dated entry, newest first (the entry the reader cares most about). */
export function basisRows(basis: ExpenseBasis): BasisRowView[] {
  return [...basis.effective]
    .sort((a, b) => b.effectiveMonth.localeCompare(a.effectiveMonth))
    .map((entry) => ({
      effectiveMonth: entry.effectiveMonth,
      label: entry.basis === null ? "Follow the fleet default" : BASIS_KIND_LABELS[entry.basis],
      isFleetDefault: entry.basis === null,
    }));
}

/** The basis in force for `asOf` (the most recent entry on or before it), or null before any entry. */
export function effectiveBasis(basis: ExpenseBasis, asOf: string): ExpenseBasisEntry | null {
  const inForce = basis.effective.filter((entry) => entry.effectiveMonth <= asOf);
  if (inForce.length === 0) return null;
  return inForce.reduce((latest, entry) => (entry.effectiveMonth > latest.effectiveMonth ? entry : latest));
}

/** Mirrors API-034's 409/422 client-side: a month, and `cur` needs a verified, active CUR source. */
export function validateSetBasis(input: { effectiveMonth: string; basis: ExpenseBasisKind | null }, hasActiveCurSource: boolean): FieldErrors {
  const errors: FieldErrors = {};
  const monthError = month(input.effectiveMonth);
  if (monthError) errors.effectiveMonth = monthError;
  if (input.basis === "cur" && !hasActiveCurSource) errors.basis = "Link and verify a CUR source first.";
  return errors;
}

// --- CUR sources ----------------------------------------------------------------------------------

export interface CurSourceRowView {
  id: string;
  bucket: string;
  prefix: string;
  region: string;
  status: CurSourceStatus;
  statusLabel: string;
  lastSuccessAt: string | null;
  lastErrorCode: string | null;
  unmappedRows: number;
  canIngest: boolean;
  exportName: string;
  awsAccountLinkId: string;
  /** Verify and disable are refused for a disabled source (409 `conflict`). */
  canVerify: boolean;
  canDisable: boolean;
}

export function curSourceRows(sources: readonly CurSource[]): CurSourceRowView[] {
  return sources.map((source) => ({
    id: source.id,
    bucket: source.bucket,
    prefix: source.prefix,
    region: source.region,
    status: source.status,
    statusLabel: CUR_SOURCE_STATUS_LABELS[source.status],
    lastSuccessAt: source.lastSuccessAt,
    lastErrorCode: source.lastErrorCode,
    unmappedRows: source.lastRuns.reduce((sum, run) => sum + run.unmappedRows, 0),
    canIngest: source.status === "active" || source.status === "failing",
    exportName: source.exportName,
    awsAccountLinkId: source.awsAccountLinkId,
    canVerify: source.status !== "disabled",
    canDisable: source.status !== "disabled",
  }));
}

export interface NewCurSourceInput {
  awsAccountLinkId: string;
  bucket: string;
  prefix: string;
  exportName: string;
  region: string;
}

export function validateNewCurSource(input: NewCurSourceInput): FieldErrors {
  return validate(
    { awsAccountLinkId: input.awsAccountLinkId, bucket: input.bucket, exportName: input.exportName, region: input.region },
    { awsAccountLinkId: [required], bucket: [required], exportName: [required], region: [required] },
  );
}

// --- CUR usage-type map ----------------------------------------------------------------------------

export interface UsageTypeRowView {
  productCode: string;
  usageTypePattern: string;
  bedrockModelId: string;
  tokenClass: TokenClass;
  verified: boolean;
  source: string;
  reviewedBy: string | null;
  reviewedOn: string | null;
}

export function usageTypeRows(types: readonly CurUsageType[]): UsageTypeRowView[] {
  return types.map((type) => ({ ...type }));
}

export interface NewUsageTypeInput {
  productCode: string;
  usageTypePattern: string;
  bedrockModelId: string;
  tokenClass: string;
  source: string;
  verified: boolean;
}

export function validateNewUsageType(input: NewUsageTypeInput): FieldErrors {
  return validate(
    {
      productCode: input.productCode,
      usageTypePattern: input.usageTypePattern,
      bedrockModelId: input.bedrockModelId,
      tokenClass: input.tokenClass,
      source: input.source,
    },
    { productCode: [required], usageTypePattern: [required], bedrockModelId: [required], tokenClass: [required], source: [required] },
  );
}

/** `verified: true` additionally needs `billing:approve` (API-035) — the create form hides the option otherwise. */
export function canRequestVerified(canApprove: boolean, requested: boolean): boolean {
  return !requested || canApprove;
}

// --- Reconciliation and finalize --------------------------------------------------------------------

export interface ReconciliationAccountView extends ExpenseReconciliationAccount {
  overMeteredDays: number;
}

export interface ReconciliationSummaryView {
  accounts: ReconciliationAccountView[];
  pendingAccountIds: string[];
  allFinal: boolean;
  unmappedRows: ExpenseUnmappedRow[];
  hasUnmapped: boolean;
}

/** Per-account and fleet-wide read of a reconciliation response (SCR-012's reconciliation table). */
export function reconciliationSummary(reconciliation: ExpenseReconciliation): ReconciliationSummaryView {
  const accounts: ReconciliationAccountView[] = reconciliation.accounts.map((account) => ({
    ...account,
    overMeteredDays: account.items.filter((item) => item.unattributedMicroUsd < 0).length,
  }));
  const pendingAccountIds = accounts.filter((account) => !account.final).map((account) => account.awsAccountId);
  const unmappedRows = accounts.flatMap((account) => account.unmapped);
  return { accounts, pendingAccountIds, allFinal: pendingAccountIds.length === 0, unmappedRows, hasUnmapped: unmappedRows.length > 0 };
}

export type ExpensePeriodView =
  | { kind: "loading" }
  | { kind: "error"; error: ApiError }
  | { kind: "empty" }
  | { kind: "finalized"; snapshot: ExpenseSnapshot }
  | { kind: "reconciling"; summary: ReconciliationSummaryView };

export interface ExpensePeriodState {
  loading: boolean;
  error: ApiError | null;
  snapshot: ExpenseSnapshot | null;
  reconciliation: ExpenseReconciliation | null;
}

/** The screen's one state (SCR-012): loading, error, empty, finalized, or reconciling with its warnings. */
export function expensePeriodView(state: ExpensePeriodState): ExpensePeriodView {
  if (state.error) return { kind: "error", error: state.error };
  if (state.loading && !state.snapshot && !state.reconciliation) return { kind: "loading" };
  if (state.snapshot) return { kind: "finalized", snapshot: state.snapshot };
  if (!state.reconciliation || state.reconciliation.accounts.length === 0) return { kind: "empty" };
  return { kind: "reconciling", summary: reconciliationSummary(state.reconciliation) };
}

export interface ExpensePeriodData {
  snapshot: ExpenseSnapshot | null;
  reconciliation: ExpenseReconciliation;
}

/**
 * Load one org-period (SCR-012): its finalized snapshot (null while none exists, 404) and the
 * reconciliation for exactly that period (API-036's required `?period=`).
 */
export async function loadExpensePeriod(client: ApiClient, orgId: string, period: string): Promise<ExpensePeriodData> {
  const snapshot = await client
    .get("/v1/admin/orgs/{orgId}/billing-periods/{period}/expense", { path: { orgId, period } })
    .catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    });
  const reconciliation = await client.get("/v1/admin/orgs/{orgId}/reconciliation/expense", { path: { orgId }, query: { period } });
  return { snapshot, reconciliation };
}

export interface FinalizeInput {
  force: boolean;
  reason: string;
}

/** `force` needs a 10–500 character reason (API-036 `force_reason_required`). */
export function validateFinalizeRequest(input: FinalizeInput): FieldErrors {
  if (!input.force) return {};
  const problem = forceReason(input.reason);
  return problem ? { reason: problem } : {};
}

export interface FinalizeRequestBody {
  force: boolean;
  reason?: string;
}

export function finalizeRequestBody(input: FinalizeInput): FinalizeRequestBody {
  return input.force ? { force: true, reason: input.reason.trim() } : { force: false };
}

// ---- Finalize refusals (API-036) ----

export interface FinalizeRefusal {
  code: string;
  message: string;
  /** Row-level or context detail the API named: the pending month, the unmapped rows, the basis. */
  details: readonly string[];
  /** Whether ticking "force" (with a reason) can get past this refusal (API-036: `cur_not_final`
   *  and `cur_unmapped` are "both overridable with force + reason"; `basis_mismatch` too, and the
   *  snapshot records it; `period_open` never is — the ledger period must close first). */
  forcible: boolean;
  retryable: boolean;
}

const FINALIZE_FORCIBLE_CODES = new Set(["cur_not_final", "cur_unmapped", "basis_mismatch"]);

/** Turns a `finalizeExpense` failure into what the dialog shows, exactly as `billing/model.ts`'s
 *  `closeRefusalFrom` does for the Close dialog (SCR-006/SCR-012 share the same refusal shape). */
export function finalizeRefusalFrom(error: ApiError): FinalizeRefusal {
  return {
    code: error.code,
    message: error.message,
    details: error.details.map((detail) => detail.message ?? detail.field ?? detail.code),
    forcible: FINALIZE_FORCIBLE_CODES.has(error.code),
    retryable: error.retryable,
  };
}

// ---- the redesign's additions ----

export type ChipTone = "green" | "amber" | "grey" | "red" | "blue";

export const CUR_SOURCE_STATUS_TONES: Readonly<Record<CurSourceStatus, ChipTone>> = {
  pending_verification: "amber",
  active: "green",
  failing: "red",
  disabled: "grey",
};

export const TOKEN_CLASS_LABELS: Readonly<Record<TokenClass, string>> = {
  input: "Input",
  output: "Output",
  cache_read: "Cache read",
  cache_write_5m: "Cache write (5 min)",
  cache_write_1h: "Cache write (1 hour)",
};

export const TOKEN_CLASSES = Object.keys(TOKEN_CLASS_LABELS) as TokenClass[];

/** A customer's basis choice in the form: its own list / CUR, or following the fleet default. */
export type OrgBasisChoice = ExpenseBasisKind | "fleet";

export const ORG_BASIS_OPTIONS: readonly { value: OrgBasisChoice; label: string }[] = [
  { value: "fleet", label: "Fleet default" },
  { value: "list", label: "AWS list price" },
  { value: "cur", label: "Actual AWS bill" },
];

export const FLEET_BASIS_OPTIONS: readonly { value: ExpenseBasisKind; label: string }[] = [
  { value: "list", label: "AWS list price" },
  { value: "cur", label: "Actual AWS bill" },
];

export function orgBasisValue(choice: OrgBasisChoice): ExpenseBasisKind | null {
  return choice === "fleet" ? null : choice;
}

/** A new basis takes effect from the next month by default (the current one has usage priced already). */
export function defaultEffectiveMonth(now: Date): string {
  return addMonths(currentMonth(now), 1);
}

/** The basis in force this month, in words, or null before any entry. */
export function basisInForceLabel(basis: ExpenseBasis, now: Date): string | null {
  const entry = effectiveBasis(basis, currentMonth(now));
  if (!entry) return null;
  return entry.basis === null ? "Follows the fleet default" : BASIS_KIND_LABELS[entry.basis];
}

/** Whether a source counts as verified for the `cur` basis (API-034: `active` or `failing`). */
export function hasVerifiedCurSource(rows: readonly Pick<CurSourceRowView, "status">[]): boolean {
  return rows.some((row) => row.status === "active" || row.status === "failing");
}

export interface CurAccountOption {
  linkId: string;
  label: string;
  awsAccountId: string;
}

/** The customer's AWS account links that can hold a CUR source: linked with the `cur` capability. */
export function curAccountOptions(links: readonly AwsAccountLink[]): CurAccountOption[] {
  return links
    .filter((link) => link.capabilities.includes("cur"))
    .map((link) => ({
      linkId: link.id,
      awsAccountId: link.awsAccountId,
      label: `${link.awsAccountId}${link.verifiedAt ? "" : " (not verified yet)"}`,
    }));
}

/** What the bootstrap stack created for a link: the only bucket a CUR source may name (FLOW-007). */
export function suggestedCurSource(option: Pick<CurAccountOption, "linkId" | "awsAccountId">): NewCurSourceInput {
  return { awsAccountLinkId: option.linkId, bucket: `nexus-cur-${option.linkId}-${option.awsAccountId}`, prefix: "cur", exportName: "nexus-cur", region: "us-east-1" };
}

/** "bucket/prefix/" as the export's location. */
export function curLocation(row: Pick<CurSourceRowView, "bucket" | "prefix">): string {
  const prefix = row.prefix.replace(/^\/+/, "");
  return prefix === "" ? `${row.bucket}/` : `${row.bucket}/${prefix}${prefix.endsWith("/") ? "" : "/"}`;
}

const RESTATE_FORCIBLE_CODES = new Set(["restatement_held"]);

/** A restate refusal (API-036): only `restatement_held` can be forced. */
export function restateRefusalFrom(error: ApiError): FinalizeRefusal {
  return { ...finalizeRefusalFrom(error), forcible: RESTATE_FORCIBLE_CODES.has(error.code) };
}
